import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { LibraryNavigation } from "../components/navigation/LibraryNavigation";
import { 
  Ticket, 
  Calendar,
  MapPin,
  Sparkles,
  Download,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";

const MyTickets = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (e) {
      console.error("Failed to fetch tickets:", e);
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = async (ticketId, ticketCode) => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/qr`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stardomfans-ticket-${ticketCode}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download QR", e);
    }
  };

  // Inline QR blob URLs cached per ticket so the QR endpoint (which requires auth)
  // can be displayed in an <img> without exposing the bearer token in URLs.
  const [qrBlobs, setQrBlobs] = useState({});
  const loadInlineQR = async (ticketId) => {
    if (qrBlobs[ticketId]) return;
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/qr`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setQrBlobs(prev => ({ ...prev, [ticketId]: url }));
    } catch (e) {
      console.error("Inline QR fetch failed", e);
    }
  };

  const downloadEticket = async (ticketId) => {
    // Opens the HTML e-ticket in a new window. The user can print or "Save as PDF".
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      console.error("Failed to download e-ticket", e);
    }
  };

  const downloadPDF = async (ticketId, ticketCode) => {
    // True binary PDF with embedded HMAC-signed QR — printable + anti-forgery.
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stardomfans-ticket-${ticketCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download ticket PDF", e);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="my-tickets-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Button 
            onClick={() => navigate("/dashboard")}
            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
          >
            Dashboard
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-6">
        <LibraryNavigation />
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">My Tickets</h1>
            <p className="text-[#8A92A0]">Your purchased event tickets with scannable QR codes</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="card p-12 text-center">
              <Ticket className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
              <h2 className="font-['Unbounded'] text-xl text-white mb-2">No Tickets Yet</h2>
              <p className="text-[#8A92A0] mb-6">You haven't purchased any tickets yet.</p>
              <Button 
                onClick={() => navigate("/discover")}
                className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
              >
                Browse Events
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => {
                const isExpanded = expandedTicket === ticket.ticket_id;
                return (
                  <div 
                    key={ticket.ticket_id} 
                    className="card overflow-hidden border border-[#1A2030]"
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              ticket.status === "valid" 
                                ? "bg-[#8DB87A]/10 text-[#8DB87A]" 
                                : ticket.status === "used"
                                ? "bg-[#A1A1AA]/10 text-[#8A92A0]"
                                : "bg-[#F87171]/10 text-[#F87171]"
                            }`}>
                              {ticket.status.toUpperCase()}
                            </span>
                            <span className="font-mono text-sm text-[#EC008C]">{ticket.ticket_code}</span>
                          </div>
                          
                          {ticket.event ? (
                            <>
                              <h3 className="font-['Unbounded'] text-xl font-semibold text-white mb-2">
                                {ticket.event.title}
                              </h3>
                              <div className="space-y-1 text-sm">
                                <p className="text-[#8A92A0] flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  {ticket.event.event_date} at {ticket.event.event_time}
                                </p>
                                <p className="text-[#8A92A0] flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  {ticket.event.venue}, {ticket.event.location}
                                </p>
                              </div>
                            </>
                          ) : (
                            <p className="text-[#8A92A0]">Event details unavailable</p>
                          )}
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                          <Button
                            onClick={() => downloadPDF(ticket.ticket_id, ticket.ticket_code)}
                            size="sm"
                            className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                            data-testid={`download-pdf-${ticket.ticket_id}`}
                            title="Signed PDF with embedded QR — printable, anti-forgery"
                          >
                            <Download className="w-4 h-4 mr-1" /> PDF
                          </Button>
                          <Button
                            onClick={() => downloadEticket(ticket.ticket_id)}
                            size="sm"
                            variant="outline"
                            className="border-[#1A2030] text-white hover:bg-[#1A2030]"
                            data-testid={`download-ticket-${ticket.ticket_id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> HTML
                          </Button>
                          <Button
                            onClick={() => downloadQR(ticket.ticket_id, ticket.ticket_code)}
                            size="sm"
                            variant="outline"
                            className="border-[#1A2030] text-white hover:bg-[#1A2030]"
                            data-testid={`download-qr-${ticket.ticket_id}`}
                          >
                            <Download className="w-4 h-4 mr-1" /> QR
                          </Button>
                          <button
                            onClick={() => {
                              const willExpand = expandedTicket !== ticket.ticket_id;
                              setExpandedTicket(willExpand ? ticket.ticket_id : null);
                              if (willExpand) loadInlineQR(ticket.ticket_id);
                            }}
                            className="p-2 rounded-lg bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white"
                            data-testid={`expand-qr-${ticket.ticket_id}`}
                          >
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Expandable QR Code */}
                    {isExpanded && (
                      <div className="border-t border-[#1A2030] p-6 bg-[#000000] flex flex-col items-center gap-3" data-testid={`qr-panel-${ticket.ticket_id}`}>
                        {qrBlobs[ticket.ticket_id] ? (
                          <img 
                            src={qrBlobs[ticket.ticket_id]}
                            alt={`QR Code for ${ticket.ticket_code}`}
                            className="w-48 h-48 rounded-lg"
                          />
                        ) : (
                          <div className="w-48 h-48 rounded-lg bg-[#0A0E18] flex items-center justify-center text-[#8A92A0]">
                            Loading QR…
                          </div>
                        )}
                        <p className="font-mono text-lg font-bold text-[#EC008C] tracking-widest break-all text-center px-4">{ticket.ticket_code}</p>
                        <p className="text-xs text-[#6A7280] text-center">Present this QR (or e-ticket) at the venue. HMAC-signed — single-use anti-counterfeit.</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default MyTickets;
